/**
 * Effective-capability store (PRD AC-04-021).
 *
 * Mirrors `GET /auth/me/capabilities`: the union of the user's application-
 * role baseline and their granular-role assignments (direct + via groups).
 * Used for nav/route gating only — the server re-checks every request, so a
 * stale set can never widen real access.
 *
 * The set is keyed to the user id; login as someone else (or a role change)
 * refetches. A 403 CAPABILITY_REQUIRED elsewhere should call `invalidate()`
 * so gating catches up with a mid-session revoke.
 */

import { create } from 'zustand';
import apiClient from '@/api/client';

interface CapabilityState {
  userId: string | null;
  capabilities: string[] | null; // null = not loaded yet
  loading: boolean;
  load: (userId: string) => Promise<void>;
  invalidate: () => void;
}

export const useCapabilityStore = create<CapabilityState>((set, get) => ({
  userId: null,
  capabilities: null,
  loading: false,

  load: async (userId: string) => {
    const state = get();
    if (state.loading || (state.userId === userId && state.capabilities !== null)) return;
    set({ loading: true, userId });
    try {
      const { data } = await apiClient.get<{ capabilities: string[] }>('/auth/me/capabilities');
      set({ capabilities: data.capabilities, loading: false, userId });
    } catch {
      // Fail closed for capability-gated extras; role-gated UI is unaffected.
      set({ capabilities: [], loading: false, userId });
    }
  },

  invalidate: () => set({ capabilities: null, userId: null, loading: false }),
}));

export function hasAnyCapability(capabilities: string[] | null, keys: string[]): boolean {
  if (!capabilities || keys.length === 0) return false;
  return keys.some((k) => capabilities.includes(k));
}
